import { useState, useEffect } from "react";
import { withAppHOC } from "../../../hoc";
import PageHeading from "../../../components/PageHeader/PageHeader";
import OverlayLoader from "../../../components/OverlayLoader/OverlayLoader";
import { Grid, GridItem } from "../../../components/Grid";
import {
  FormContainer,
  Form,
  Fieldset,
  Label,
  TextInput,
  FileInput,
  MessageBox,
  CreateButton,
  ResetButton,
  NumberInput,
  Switch,
} from "../../../components/FormElements";
import API from "../../../api";
import { API_RESPONSE_TYPES, PERMISSION_TYPES } from "../../../constants";
import { getBase64, getReturnUrl } from "../../../utils";
import Numberinput from "../../../components/FormElements/NumberInput";
import { StyledSwitchOuter } from "../../../components/Styled";

const OperatorsCreate = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState("");
  const [responseMessage, setResponseMessage] = useState("");
  const [operatorName, setOperatorName] = useState("");
  const [operatorLogo, setOperatorLogo] = useState([]);
  const [operatorLogoBase64, setOperatorLogoBase64] = useState("");
  const [mastersMargin, setMastersMargin] = useState("");
  const [shouldCheckCircle, setShouldCheckCircle] = useState("No");
  const [minRechargeAmount, setMinRechargeAmount] = useState("1");
  const [maxRechargeAmount, setMaxRechargeAmount] = useState("5000");
  const [validRechargeAmount, setValidRechargeAmount] = useState("");
  const [validRofferRechargeAmount, setValidRofferRechargeAmount] =
    useState("");
  const [planApiOperatorCode, setPlanApiOperatorCode] = useState("");
  const [operatorStatus, setOperatorStatus] = useState("Active");
  const [rofferApplicable, setRofferApplicable] = useState("Yes");
  const [blockedAmounts, setBlockedAmounts] = useState("");
  const [failureCounter, setFailureCounter] = useState("0");
  const [disableMargin, setDisableMargin] = useState("Y");
  const [isWrongToRightAllowed, setIsWrongToRightAllowed] = useState("Yes");
  const [wrongToRightTime, setWrongToRightTime] = useState("");
  const [wrongToRightReversalTime, setWrongToRightReversalTime] = useState("");
  const [isBSNL, setIsBSNL] = useState("No");
  const [bsnlRtvAmounts, setBsnlRtvAmounts] = useState("");

  const resetResponseData = () => {
    setIsLoading(false);
    setResponseStatus("");
    setResponseMessage("");
  };

  const resetForm = () => {
    setOperatorName("");
    setOperatorLogo([]);
    setOperatorLogoBase64("");
    setMastersMargin("");
    setShouldCheckCircle("No");
    setMinRechargeAmount("1");
    setMaxRechargeAmount("5000");
    setValidRechargeAmount("");
    setValidRofferRechargeAmount("");
    setPlanApiOperatorCode("");
    setOperatorStatus("Active");
    setRofferApplicable("Yes");
    setBlockedAmounts("");
    setFailureCounter("0");
    setDisableMargin("Y");
    setIsWrongToRightAllowed("No");
    setWrongToRightTime("");
    setWrongToRightReversalTime("");
    setIsBSNL("No");
    setBsnlRtvAmounts("");
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setResponseStatus("");
    setResponseMessage("");
    const formData = {
      operatorName,
      operatorLogo: operatorLogoBase64,
      mastersMargin,
      shouldCheckCircle,
      minRechargeAmount,
      maxRechargeAmount,
      validRechargeAmount,
      validRofferRechargeAmount,
      planApiOperatorCode,
      operatorStatus,
      rofferApplicable,
      blockedAmounts,
      failureCounter,
      disableMargin,
      isWrongToRightAllowed,
      wrongToRightTime,
      wrongToRightReversalTime,
      isBSNL,
      bsnlRtvAmounts,
    };
    API.post("/recharge-configurations/operators", formData)
      .then((response) => {
        const { status, message } = response.data;
        setResponseStatus(status);
        setResponseMessage(message);
        if (status === API_RESPONSE_TYPES.SUCCESS) {
          resetForm();
          setTimeout(() => {
            resetResponseData();
            window.location.href = getReturnUrl(PERMISSION_TYPES.CREATE);
          }, 3000);
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILED);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (operatorLogo?.length > 0) {
      getBase64(operatorLogo[0], setOperatorLogoBase64);
    }
  }, [operatorLogo]);

  return (
    <>
      <PageHeading {...props} />
      <FormContainer>
        <Form method="POST" action="#" onSubmit={onSubmit}>
          <Grid
            columns={
              "calc(33.33% - 13.33px) calc(33.33% - 13.33px) calc(33.33% - 13.33px)"
            }
          >
            <GridItem>
              <Fieldset>
                <Label required>Operator Name</Label>
                <TextInput
                  value={operatorName}
                  onChange={setOperatorName}
                  placeholder="Please enter operator name"
                  maxLength={100}
                  disabled={isLoading}
                />
              </Fieldset>
            </GridItem>
            <GridItem>
              <Fieldset>
                <Label required>Admin Margin</Label>
                <Numberinput
                  value={mastersMargin}
                  onChange={setMastersMargin}
                  placeholder="Please enter admin margin"
                  maxLength={100}
                  disabled={isLoading}
                />
              </Fieldset>
            </GridItem>
          </Grid>
          <Fieldset>
            <Label>Operator Logo</Label>
            <FileInput
              files={operatorLogo}
              onChange={setOperatorLogo}
              accept="image/*"
              disabled={isLoading}
            />
          </Fieldset>
          <Grid columns={"calc(50% - 10px) calc(50% - 10px)"}>
            <GridItem>
              <Fieldset>
                <Label required>Min Amount</Label>
                <NumberInput
                  value={minRechargeAmount}
                  onChange={setMinRechargeAmount}
                  placeholder="Please enter min amount"
                  maxLength={100}
                  disabled={isLoading}
                />
              </Fieldset>
            </GridItem>
            <GridItem>
              <Fieldset>
                <Label required>Max Amount</Label>
                <NumberInput
                  value={maxRechargeAmount}
                  onChange={setMaxRechargeAmount}
                  placeholder="Please enter max amount"
                  maxLength={100}
                  disabled={isLoading}
                />
              </Fieldset>
            </GridItem>
            <GridItem>
              <Fieldset>
                <Label>Plan API Code</Label>
                <TextInput
                  value={planApiOperatorCode}
                  onChange={setPlanApiOperatorCode}
                  placeholder="Please enter plan api operator code"
                  maxLength={100}
                  disabled={isLoading}
                />
              </Fieldset>
            </GridItem>
            <GridItem>
              <Fieldset>
                <Label required>Max Seller Failure Count</Label>
                <NumberInput
                  value={failureCounter}
                  onChange={setFailureCounter}
                  placeholder="Please enter max seller failure count"
                  disabled={isLoading}
                />
              </Fieldset>
            </GridItem>
          </Grid>
          <Fieldset>
            <Label>Valid Recharge Amounts</Label>
            <NumberInput
              value={validRechargeAmount}
              onChange={setValidRechargeAmount}
              placeholder="Please enter amounts seperated by comma(,)"
              disabled={isLoading}
              pattern="[0-9.,]*"
            />
          </Fieldset>
          <Fieldset>
            <Label>Valid Roffer Recharge Amounts</Label>
            <NumberInput
              value={validRofferRechargeAmount}
              onChange={setValidRofferRechargeAmount}
              placeholder="Please enter amounts seperated by comma(,)"
              disabled={isLoading}
              pattern="[0-9.,]*"
            />
          </Fieldset>
          <Fieldset>
            <Label>Blocked Amounts</Label>
            <NumberInput
              value={blockedAmounts}
              onChange={setBlockedAmounts}
              placeholder="Please enter amounts seperated by commas"
              disabled={isLoading}
              pattern="^\d+(,\d+)*$"
            />
          </Fieldset>
          <Grid>
            <GridItem>
              <Fieldset>
                <Label required>Operator Status</Label>
                <StyledSwitchOuter>
                  <Switch
                    value={operatorStatus === "Active"}
                    onChange={(value) =>
                      setOperatorStatus(value === true ? `Active` : `Inactive`)
                    }
                  />
                </StyledSwitchOuter>
              </Fieldset>
            </GridItem>
            <GridItem>
              <Fieldset>
                <Label required>Roffer Status</Label>
                <StyledSwitchOuter>
                  <Switch
                    value={rofferApplicable === "Yes"}
                    onChange={(value) =>
                      setRofferApplicable(value === true ? `Yes` : `No`)
                    }
                  />
                </StyledSwitchOuter>
              </Fieldset>
            </GridItem>
            <GridItem>
              <Fieldset>
                <Label required>Circle Check</Label>
                <StyledSwitchOuter>
                  <Switch
                    value={shouldCheckCircle === "Yes"}
                    onChange={(value) =>
                      setShouldCheckCircle(value === true ? `Yes` : `No`)
                    }
                  />
                </StyledSwitchOuter>
              </Fieldset>
            </GridItem>
            <GridItem>
              <Fieldset>
                <Label required>Disable Margin if Backup Success</Label>
                <StyledSwitchOuter>
                  <Switch
                    value={disableMargin === "Y"}
                    onChange={(value) =>
                      setDisableMargin(value === true ? `Y` : `N`)
                    }
                  />
                </StyledSwitchOuter>
              </Fieldset>
            </GridItem>
          </Grid>
          <Grid>
            <GridItem>
              <Fieldset>
                <Label required>Is Wrong to Right Allowed?</Label>
                <StyledSwitchOuter>
                  <Switch
                    value={isWrongToRightAllowed === "Yes"}
                    onChange={(value) =>
                      setIsWrongToRightAllowed(value === true ? `Yes` : `No`)
                    }
                  />
                </StyledSwitchOuter>
              </Fieldset>
            </GridItem>
            <GridItem>
              <Fieldset>
                <Label required>Is BSNL?</Label>
                <StyledSwitchOuter>
                  <Switch
                    value={isBSNL === "Yes"}
                    onChange={(value) =>
                      setIsBSNL(value === true ? `Yes` : `No`)
                    }
                  />
                </StyledSwitchOuter>
              </Fieldset>
            </GridItem>
            <GridItem></GridItem>
            <GridItem></GridItem>
          </Grid>
          {isWrongToRightAllowed === "Yes" && (
            <Grid>
              <GridItem>
                <Fieldset>
                  <Label required>Wrong to Right Time</Label>

                  <NumberInput
                    value={wrongToRightTime}
                    onChange={setWrongToRightTime}
                    placeholder="Please enter time in minutes"
                    disabled={isLoading}
                  />
                </Fieldset>
              </GridItem>
              <GridItem>
                <Fieldset>
                  <Label required>Wrong to Right Reversal Time</Label>
                  <NumberInput
                    value={wrongToRightReversalTime}
                    onChange={setWrongToRightReversalTime}
                    placeholder="Please enter time in minutes"
                    disabled={isLoading}
                  />
                </Fieldset>
              </GridItem>
            </Grid>
          )}
          {isBSNL === "Yes" && (
            <Fieldset>
              <Label required>Topup to STV Amounts</Label>
              <NumberInput
                value={bsnlRtvAmounts}
                onChange={setBsnlRtvAmounts}
                placeholder="Please enter amounts seperated by comma(,)"
                disabled={isLoading}
                pattern="[0-9.,]*"
              />
            </Fieldset>
          )}
          {responseStatus && (
            <Fieldset>
              <MessageBox status={responseStatus} message={responseMessage} />
            </Fieldset>
          )}
          <Fieldset>
            <CreateButton disabled={isLoading} type="submit">
              Create
            </CreateButton>
            <ResetButton disabled={isLoading} type="button" onClick={resetForm}>
              Reset
            </ResetButton>
          </Fieldset>
        </Form>
        <OverlayLoader showLoader={isLoading} />
      </FormContainer>
    </>
  );
};

export default withAppHOC(OperatorsCreate);
